import React, { useState, useEffect, useRef } from 'react';
import { X, MessageCircle, Send, ArrowLeft, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatWidget = ({ isOpen, onClose, autoOpened = false }) => {
  const [step, setStep] = useState('welcome'); // welcome, urgency, phone, message, operator
  const [selectedService, setSelectedService] = useState(null);
  const [selectedUrgency, setSelectedUrgency] = useState(null);
  const [customerPhone, setCustomerPhone] = useState('');
  const [userMessage, setUserMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const messagesEndRef = useRef(null);

  const services = [
    'Cancer Pain Management',
    'Neuro Palliative Care',
    'Stroke & Brain Injury Care',
    'Dementia Care',
    'Hospice Care'
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (isOpen && chatMessages.length === 0) {
      // Initial welcome message
      setTimeout(() => {
        setChatMessages([{
          type: 'bot',
          text: 'Hi! How can we help you today? Please choose a service.',
          timestamp: new Date()
        }]);
      }, 500);
    }
  }, [isOpen]);

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    
    // Add user selection to chat
    setChatMessages(prev => [...prev, {
      type: 'user',
      text: service,
      timestamp: new Date()
    }]);

    // Bot response
    setTimeout(() => {
      setChatMessages(prev => [...prev, {
        type: 'bot',
        text: 'What type of care do you need?',
        timestamp: new Date()
      }]);
      setStep('urgency');
    }, 600);
  };

  const handleUrgencySelect = (urgency) => {
    setSelectedUrgency(urgency);
    
    // Add user selection to chat
    setChatMessages(prev => [...prev, {
      type: 'user',
      text: urgency,
      timestamp: new Date()
    }]);

    // Bot response - ask for phone number
    setTimeout(() => {
      setChatMessages(prev => [...prev, {
        type: 'bot',
        text: 'Please provide your phone number so our team can reach you.',
        timestamp: new Date()
      }]);
      setStep('phone');
    }, 600);
  };

  const handlePhoneSubmit = (e) => {
    e.preventDefault();
    
    if (!customerPhone.trim() || customerPhone.length < 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    // Add phone to chat
    setChatMessages(prev => [...prev, {
      type: 'user',
      text: customerPhone,
      timestamp: new Date()
    }]);

    // Bot response - ask for message
    setTimeout(() => {
      setChatMessages(prev => [...prev, {
        type: 'bot',
        text: 'Please describe your situation or any specific concerns. Our team will respond shortly.',
        timestamp: new Date()
      }]);
      setStep('message');
    }, 600);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!userMessage.trim()) return;

    // Add user message to chat
    setChatMessages(prev => [...prev, {
      type: 'user',
      text: userMessage,
      timestamp: new Date()
    }]);

    const messageText = userMessage;
    setUserMessage(''); // Clear input immediately

    try {
      // Save conversation to backend API
      const response = await axios.post(`${API}/chat`, {
        service: selectedService,
        urgency: selectedUrgency,
        phone: customerPhone,
        message: messageText
      });

      console.log('Chat message saved to backend:', response.data);

      // Bot response - connecting to operator
      setTimeout(() => {
        setChatMessages(prev => [...prev, {
          type: 'bot',
          text: `Thank you for reaching out. We've received your ${selectedUrgency.toLowerCase()} request for ${selectedService}. Our team will contact you shortly at the phone number you provide. You can also call us directly at 9244093291 for immediate assistance.`,
          timestamp: new Date()
        }]);
        setStep('operator');

        // Show success toast
        toast.success('Message sent successfully!', {
          description: 'Our team will contact you shortly.'
        });
      }, 800);

    } catch (error) {
      console.error('Error saving chat message:', error);
      
      // Show error toast but still proceed with UI
      toast.error('Message saved locally', {
        description: 'We will still receive your message. Our team will contact you soon.'
      });

      // Still show operator state even if API fails
      setTimeout(() => {
        setChatMessages(prev => [...prev, {
          type: 'bot',
          text: `Thank you for reaching out. We've received your ${selectedUrgency.toLowerCase()} request for ${selectedService}. Our team will contact you shortly.`,
          timestamp: new Date()
        }]);
        setStep('operator');
      }, 800);
    }
  };

  const handleReset = () => {
    setStep('welcome');
    setSelectedService(null);
    setSelectedUrgency(null);
    setCustomerPhone('');
    setUserMessage('');
    setChatMessages([{
      type: 'bot',
      text: 'Hi! How can we help you today? Please choose a service.',
      timestamp: new Date()
    }]);
  };

  const handleClose = () => {
    if (autoOpened) {
      localStorage.setItem('chatAutoOpened', 'true');
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col">
      {/* Chat Window */}
      <Card className="w-[380px] h-[600px] shadow-2xl border-2 border-blue-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-5 duration-300 max-[440px]:w-[calc(100vw-2rem)] max-[440px]:h-[calc(100vh-2rem)] max-[440px]:fixed max-[440px]:inset-4 max-[440px]:bottom-4 max-[440px]:right-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-full">
              <MessageCircle size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Dard Satya</h3>
              <p className="text-xs text-blue-100">Where comfort meets care</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="hover:bg-white/20 p-2 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 bg-gradient-to-b from-blue-50/30 to-white space-y-4">
          {chatMessages.map((msg, index) => (
            <div
              key={index}
              className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  msg.type === 'user'
                    ? 'bg-blue-600 text-white rounded-br-none'
                    : 'bg-white border border-blue-200 text-gray-800 rounded-bl-none shadow-sm'
                }`}
              >
                <p className="text-sm leading-relaxed">{msg.text}</p>
              </div>
            </div>
          ))}

          {/* Service Selection Buttons */}
          {step === 'welcome' && chatMessages.length > 0 && (
            <div className="space-y-2 animate-in fade-in duration-500">
              {services.map((service, index) => (
                <button
                  key={index}
                  onClick={() => handleServiceSelect(service)}
                  className="w-full bg-white hover:bg-blue-50 border-2 border-blue-200 hover:border-blue-400 text-gray-800 px-4 py-3 rounded-xl text-left font-medium transition-all hover:shadow-md hover:translate-x-1"
                >
                  {service}
                </button>
              ))}
            </div>
          )}

          {/* Urgency Selection Buttons */}
          {step === 'urgency' && (
            <div className="space-y-2 animate-in fade-in duration-500">
              <button
                onClick={() => handleUrgencySelect('Urgent')}
                className="w-full bg-red-50 hover:bg-red-100 border-2 border-red-300 hover:border-red-500 text-red-800 px-4 py-3 rounded-xl font-semibold transition-all hover:shadow-md"
              >
                🚨 Urgent - Need immediate assistance
              </button>
              <button
                onClick={() => handleUrgencySelect('Normal')}
                className="w-full bg-blue-50 hover:bg-blue-100 border-2 border-blue-300 hover:border-blue-500 text-blue-800 px-4 py-3 rounded-xl font-semibold transition-all hover:shadow-md"
              >
                📅 Normal - Schedule consultation
              </button>
            </div>
          )}

          {/* Phone Number Input */}
          {step === 'phone' && (
            <div className="animate-in fade-in duration-500">
              <form onSubmit={handlePhoneSubmit} className="space-y-3">
                <Input
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="Enter 10-digit phone number"
                  className="border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                  maxLength={10}
                  pattern="[0-9]{10}"
                  autoFocus
                />
                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Continue
                </Button>
              </form>
            </div>
          )}

          {/* Operator Connected State */}
          {step === 'operator' && (
            <div className="text-center space-y-3 animate-in fade-in duration-500">
              <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4">
                <div className="bg-green-600 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <User className="text-white" size={24} />
                </div>
                <p className="text-green-800 font-semibold mb-2">Message Received!</p>
                <p className="text-sm text-gray-600">
                  Service: <span className="font-medium text-gray-800">{selectedService}</span>
                </p>
                <p className="text-sm text-gray-600">
                  Priority: <span className="font-medium text-gray-800">{selectedUrgency}</span>
                </p>
              </div>
              <Button
                onClick={handleReset}
                variant="outline"
                className="w-full border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                <ArrowLeft size={16} className="mr-2" />
                Start New Conversation
              </Button>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        {step === 'message' && (
          <div className="border-t border-blue-200 p-4 bg-white">
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <Input
                value={userMessage}
                onChange={(e) => setUserMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                autoFocus
              />
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4"
                disabled={!userMessage.trim()}
              >
                <Send size={18} />
              </Button>
            </form>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ChatWidget;
